import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { login, reset, selectCurrentUser, selectCurrentToken, selectAuthLoading, selectAuthError, selectAuthSuccess, selectAuthMessage } from '../features/auth/authSlice';
import { useToast } from '../contexts/ToastContext';
import OmTextField from '../components/ui/OmTextField';
import OmButton from '../components/ui/OmButton';
import OMLogo from '../assets/OM-logo.png';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    identifier: location.state?.username || '',
    password: ''
  });
  const toast = useToast();
  const messageShownRef = useRef(false);

  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isLoading = useSelector(selectAuthLoading);
  const isError = useSelector(selectAuthError);
  const isSuccess = useSelector(selectAuthSuccess);
  const message = useSelector(selectAuthMessage);

  const from = location.state?.from?.pathname || '/dashboard';
  const successMessage = location.state?.message;

  // Clear any stale auth errors when component mounts
  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  // Show success message from password change - prevent infinite loop
  useEffect(() => {
    if (successMessage && !messageShownRef.current) {
      messageShownRef.current = true;
      toast.showSuccess(successMessage);
      // Clear the message from location state without triggering re-render
      const newState = { ...location.state };
      delete newState.message;
      navigate(location.pathname, { replace: true, state: newState });
    }
  }, [successMessage, toast, navigate]);

  // Reset message shown flag when successMessage changes or component unmounts
  useEffect(() => {
    if (!successMessage) {
      messageShownRef.current = false;
    }
  }, [successMessage]);

  useEffect(() => {
    // Only show errors that occur after mount (from actual login attempts)
    if (isError && message && isLoading === false) {
      toast.showError(message);
    }

    if (isSuccess) {
      // Check if password change is required FIRST
      if (user && user.mustChangePassword) {
        toast.showInfo('Please change your temporary password to continue');
        // Navigate directly to change-password page with user state
        navigate('/change-password', { 
          replace: true,
          state: { 
            userId: user.userId, 
            username: user.username,
            fromLogin: true 
          }
        });
      } else if (user && token) {
        // Only navigate to dashboard if user has a valid token
        toast.showSuccess('Login successful');
        navigate(from, { replace: true });
      }
      // If user exists but no token and no mustChangePassword, stay on login
    }

    dispatch(reset());
  }, [user, token, isError, isSuccess, message, navigate, dispatch, toast, from]);

  const handleChange = (name, e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.identifier?.trim()) {
      toast.showError('Username or email is required');
      return;
    }

    if (!formData.password?.trim()) {
      toast.showError('Password is required');
      return;
    }

    dispatch(login({
      identifier: formData.identifier.trim(),
      password: formData.password
    }));
  };

  return (
    <div className="min-h-screen bg-silver-mist flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo and Header Section */}
        <div className="flex flex-col items-center space-y-6">
          <div className="flex-shrink-0">
            <img 
              src={OMLogo} 
              alt="Old Mutual Logo" 
              className="h-16 w-auto"
            />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-2xl md:text-3xl font-display font-bold text-deep-jungle tracking-tight">
              Corporate Claims Portal
            </h1>
            <p className="text-steel text-sm font-medium">
              Sign in to access your account
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div 
          className="bg-crystal-white shadow-lg border border-silver-mist mx-4 sm:mx-0 overflow-hidden"
          style={{ borderRadius: '24px' }}
        >
          <div className="px-6 py-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <OmTextField
                  label="Username or Email"
                  type="text"
                  placeholder="Enter your username or email"
                  value={formData.identifier}
                  onChange={(e) => handleChange('identifier', e)}
                  required
                  leadingIcon="person"
                />

                <OmTextField
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e)}
                  required
                  leadingIcon="lock"
                />
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-semibold text-fresh-leaf hover:text-forest-fern transition-all duration-300 hover:underline underline-offset-4 decoration-2"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="pt-2">
                <OmButton
                  type="submit"
                  disabled={isLoading}
                  className="w-full justify-center text-base font-medium py-3 rounded-2xl"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </OmButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
